import { RefObject, useEffect, useState } from 'react';

/**
 * Custom React hook that observes the size (width and height) of one or more HTML elements
 * using the ResizeObserver API. Returns the latest width and height of the observed elements.
 *
 * @template T - The type of HTMLElement to observe.
 * @param refs - An array of React ref objects pointing to the elements to observe.
 * @returns An object containing the current `width` and `height` of the observed elements.
 *
 * @remarks
 * - If multiple refs are provided, the hook will update the width and height based on the last observed element.
 * - The hook observes the parent element if available, otherwise it observes the element itself.
 * - The hook automatically disconnects all observers on component unmount.
 *
 * @example
 * ```tsx
 * const imgRef = useRef<HTMLImageElement>(null);
 * const { width, height } = useImageSizeObserver([imgRef]);
 * ```
 */
export const useImageSizeObserver = (refs: Array<RefObject<HTMLElement | SVGSVGElement>>) => {
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (!refs || refs.length == 0) return;

    const observables: ResizeObserver[] = [];
    for (const r of refs) {
      const element = r?.current;
      if (!element) return;

      const elementToObserve = element.parentElement ?? element;

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentRect) {
            setWidth(entry.contentRect.width);
            setHeight(entry.contentRect.height);
          }
        }
      });

      resizeObserver.observe(elementToObserve);

      // Initial width and height
      if (elementToObserve instanceof SVGSVGElement) {
        const rect = elementToObserve.getBoundingClientRect();
        setWidth(rect.width);
        setHeight(rect.height);
      } else if (elementToObserve instanceof HTMLElement) {
        setWidth(elementToObserve.offsetWidth);
        setHeight(elementToObserve.offsetHeight);
      }

      observables.push(resizeObserver);
    }

    return () => {
      observables.map(a => a.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { width, height };
};
